import validator from 'validator';

/**
 * Wrapper function for `JSON.parse()` that will return `null` if the `payload` is `undefined`, `null`, 
 * an empty string, or invalid JSON.
 * @param {string} value The string to parse.
 * @returns {object | null} The parsed JSON as an object or `null`.
 */
export function parseJSON(value) {
  if (typeof value !== "string") {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

/**
 * Checks if a value is valid within an enumeration-like object.
 * @param {any} value The value to check for validity within the enumeration-like object.
 * @param {object} enumObject The enumeration-like object to validate against.
 * @returns {boolean} `true` if the value is valid within the enumeration-like object, otherwise `false`.
 */
export function isValidEnumValue(value, enumObject) {
  return Object.values(enumObject).includes(value);
}

export function isPhoneNumberE164(phoneNumber) {
  return /^\+[1-9]\d{10,14}$/.test(phoneNumber);
}

/**
 * @param {import("./types").AdvisorAppointmentBookingData} data
 * @returns {{ field: string, message: string }[]} Array of error objects
 */
export function validateAdvisorAppointmentBooking(data) {
  const errors = [];

  if (!data.first_name) {
    errors.push({ field: "first_name", message: "First name is required" });
  }

  if (!data.last_name) {
    errors.push({ field: "last_name", message: "Last name is required" });
  }

  if (!data.email) {
    errors.push({ field: "email", message: "Email is required" });
  } else if (!validator.isEmail(data.email)) {
    errors.push({ field: "email", message: "Invalid email" });
  }

  if (!data.phone_number) {
    errors.push({ field: "phone_number", message: "Phone number is required" });
  } else if (!isPhoneNumberE164(data.phone_number)) {
    errors.push({ field: "phone_number", message: "Invalid phone number, the format must be: +12223334444" });
  }

  if (!data.postal_code) {
    errors.push({ field: "postal_code", message: "Postal code is required" });
  } else if (!validator.isPostalCode(data.postal_code, "CA")) {
    errors.push({ field: "postal_code", message: "Invalid postal code, the format must be: A1A2B2 or A1A 2B2" });
  }
  
  return errors;
}

/**
 * Trims all strings properties on the root object.
 * @template T
 * @param {T} obj The object to trim strings from.
 * @returns {T}
 */
export function trimStrings(obj) {
  const trimmedObj = {};

  Object.keys(obj).forEach(key => {
    const value = obj[key];

    trimmedObj[key] = typeof obj[key] === "string" ? value.trim() : value;
  });

  return trimmedObj;
}

/**
 * Checks if an array has duplicate values.
 * 
 * WARNING: This function might not work properly with arrays that have non-primitive types.
 * 
 * @param {any[]} arr 
 * @returns `true` if the array has duplicates, otherwise `false`.
 */
export function hasDuplicates(arr) {
  return arr.some((item, index) => arr.indexOf(item) !== index);
}